// region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Cluster Tree
//
//  Copyright:    © 2025 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
// region Version 1.0.0.0
//    001   12.03.25 Sean Flook          GMSCM-1 Initial Revision.
// endregion Version 1.0.0.0
//
//--------------------------------------------------------------------------------------------------
// endregion header

import React, { ChangeEvent, Fragment, SyntheticEvent, useEffect, useState } from "react";

import MultiTenantClusterGetDto from "../dtos/multiTenantClusterDto";

import { Box, IconButton, InputAdornment, Menu, MenuItem, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";

import { ExpandMore, ChevronRight } from "@mui/icons-material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ClearIcon from "@mui/icons-material/Clear";

import {
  adsBlueA,
  adsDarkRed,
  adsLightGreyA,
  adsLightGreyB,
  adsPaleBlueA,
  adsRed,
  adsRed10,
  adsRed20,
} from "../utils/ADSColour";
import {
  ActionIconStyle,
  ClearSearchIconStyle,
  dataFormStyle,
  dataFormToolbarHeight,
  filterToolbarStyle,
  FormInputStyle,
  menuItemStyle,
  menuStyle,
  toolbarStyle,
  tooltipStyle,
} from "../utils/ADSStyles";
import { useTheme } from "@mui/material/styles";
import useClusterQueryStore from "../context/ClusterStore";
import useUserQueryStore from "../context/UserStore";

interface ClusterTreeProps {
  data: MultiTenantClusterGetDto[];
  selectedItem: string | undefined;
  haveErrors: boolean;
  errorIds: string[];
  onItemSelected: (id: string | undefined) => void;
  onAddItem: (type: string, id?: string | undefined) => void;
}

const ClusterTree: React.FC<ClusterTreeProps> = ({
  data,
  selectedItem,
  haveErrors,
  errorIds,
  onItemSelected,
  onAddItem,
}) => {
  const theme = useTheme();

  const currentUser = useUserQueryStore((s) => s.userQuery.currentUser);

  const clearFilter = useClusterQueryStore((s) => s.clusterQuery.clearFilter);
  const setClearFilter = useClusterQueryStore((s) => s.setClearFilter);

  const [filterBy, setFilterBy] = useState<string>("");
  const [filteredData, setFilteredData] = useState<MultiTenantClusterGetDto[]>(data);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  /**
   * Event to handle when the filter changes.
   *
   * @param newValue The new value to filter by.
   */
  const handleFilterChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value;
    setFilterBy(newValue);
    if (newValue.length > 0) {
      const filteredList: MultiTenantClusterGetDto[] = data.filter((cluster) =>
        cluster.name.toLowerCase().includes(newValue.toLowerCase())
      );
      setFilteredData(filteredList);
      const foundCurrent = filteredList.find((doc) => doc.id === selectedItem);
      if (!foundCurrent && onItemSelected) onItemSelected(undefined);
    } else {
      setFilteredData(data);
    }
  };

  /**
   * Event to handle when the clear search button is clicked.
   */
  const handleClearSearch = (): void => {
    setFilterBy("");
    setFilteredData(data);
  };

  /**
   * Event to handle when the cluster menu is clicked.
   *
   * @param event The event object.
   */
  const handleClusterMenuClick = (event: SyntheticEvent<HTMLElement | null>) => {
    setAnchorEl(event.nativeEvent.target as HTMLElement);
  };

  /**
   * Event to handle when the cluster menu is closed.
   *
   * @param event The event object.
   */
  const handleClusterMenuClose = (event: SyntheticEvent) => {
    setAnchorEl(null);
    event.stopPropagation();
  };

  /**
   * Event to handle when the add cluster menu item is clicked.
   *
   * @param event The event object.
   */
  const handleAddClusterClick = (event: SyntheticEvent): void => {
    handleClusterMenuClose(event);
    if (onAddItem) onAddItem("cluster");
  };

  /**
   * Event to handle when a node is selected.
   *
   * @param {object} event The event object.
   * @param {number} nodeId The id of the selected node.
   */
  function handleNodeSelected(event: SyntheticEvent, itemIds: string | null) {
    event.stopPropagation();
    if (onItemSelected) onItemSelected(itemIds ?? undefined);
  }

  /**
   * Method to get styling used for a tree item, based on whether usrn is current street and/or checked.
   *
   * @param {string} id The id for the document
   * @returns {object} The styling for the tree item.
   */
  const treeItemStyle = (id: string, hasError: boolean): object => {
    const currentCluster = id === selectedItem;
    if (hasError)
      return {
        backgroundColor: adsRed10,
        color: adsRed,
        borderTop: `solid ${adsLightGreyB} 1px`,
        pb: theme.spacing(1),
        pt: theme.spacing(1),
        "&:hover": {
          backgroundColor: adsRed20,
          color: adsDarkRed,
        },
      };
    else if (currentCluster)
      return {
        backgroundColor: adsPaleBlueA,
        borderTop: `solid ${adsLightGreyB} 1px`,
        pb: theme.spacing(1),
        pt: theme.spacing(1),
        "&:hover": {
          backgroundColor: adsPaleBlueA,
          color: adsBlueA,
        },
      };
    else
      return {
        backgroundColor: "inherit",
        borderTop: `solid ${adsLightGreyB} 1px`,
        pb: theme.spacing(1),
        pt: theme.spacing(1),
        "&:hover": {
          backgroundColor: adsPaleBlueA,
          color: adsBlueA,
        },
      };
  };

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  useEffect(() => {
    if (clearFilter) {
      setFilterBy("");
      setFilteredData(data);
      setClearFilter(false);
    }
  }, [clearFilter, data, setClearFilter]);

  return (
    <Fragment>
      <Box sx={{ ...toolbarStyle, height: `${dataFormToolbarHeight}px` }} id="ads-cluster-tree-toolbar">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pl: "12px", pr: "12px", pt: "8px" }}
        >
          <Typography variant="body2">{`${
            data.length === filteredData.length
              ? data.length
              : filteredData.length.toString() + " of " + data.length.toString()
          } multi-tenant clusters`}</Typography>
          <Stack direction="row" alignItems="center" justifyContent={"flex-end"} spacing={1}>
            <Tooltip title="Actions" arrow placement="right" sx={tooltipStyle}>
              <IconButton
                onClick={handleClusterMenuClick}
                aria-controls="cluster_action-menu"
                aria-haspopup="true"
                size="small"
              >
                <MoreVertIcon sx={ActionIconStyle()} />
              </IconButton>
            </Tooltip>
            <Menu
              id="configuration_action-menu"
              elevation={2}
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClusterMenuClose}
              sx={menuStyle}
            >
              {currentUser?.isSuperAdministrator && (
                <MenuItem dense onClick={handleAddClusterClick} sx={menuItemStyle(false)}>
                  <Typography variant="inherit">Add cluster</Typography>
                </MenuItem>
              )}
            </Menu>
          </Stack>
        </Stack>
      </Box>
      <Box
        sx={{
          height: "3px",
          backgroundColor: adsLightGreyA,
          borderBottom: `1px solid ${adsLightGreyB}`,
        }}
      />
      <Box sx={filterToolbarStyle} id="ads-cluster-tree-filter-toolbar">
        <TextField
          id="configuration-filter"
          label="Filter by cluster name"
          sx={FormInputStyle(false)}
          variant="outlined"
          fullWidth
          margin="dense"
          size="small"
          value={filterBy}
          onChange={handleFilterChange}
          placeholder="Filter by cluster name"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton id="btnClear" onClick={handleClearSearch} aria-label="Clear filter" size="small">
                    <ClearIcon sx={ClearSearchIconStyle(filterBy)} />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <Box sx={dataFormStyle("ClusterTree")}>
        <SimpleTreeView
          aria-label="cluster tree"
          id="cluster-tree"
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            "& .MuiTreeItem-content": {
              borderRadius: 0,
              margin: 0,
              backgroundColor: "transparent !important",
              boxShadow: "none !important",
            },
            "& .MuiTreeItem-content:hover": {
              backgroundColor: "transparent !important",
            },
            "& .MuiTreeItem-content.Mui-focused, & .MuiTreeItem-content.Mui-selected, & .MuiTreeItem-content.Mui-selected:hover":
              {
                backgroundColor: "transparent !important",
                boxShadow: "none !important",
              },
            "& .MuiTreeItem-label": {
              backgroundColor: "transparent !important",
            },
          }}
          slots={{ expandIcon: ChevronRight, collapseIcon: ExpandMore }}
          selectedItems={selectedItem}
          onSelectedItemsChange={handleNodeSelected}
        >
          {filteredData
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }))
            .map((rec, index) => {
              return (
                <TreeItem
                  key={`cluster-${rec.id}-${index}`}
                  itemId={`${rec.id}`}
                  sx={treeItemStyle(rec.id, haveErrors && errorIds.includes(rec.id))}
                  label={
                    <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1}>
                      <Typography variant="subtitle2">{rec.name}</Typography>
                    </Stack>
                  }
                />
              );
            })}
        </SimpleTreeView>
      </Box>
    </Fragment>
  );
};

export default ClusterTree;
